import { Box, IconButton, Tooltip } from '@wix/design-system';
import { useTranslation } from '@wix/wix-i18n-config';
import { DismissSmall, ExternalLinkSmall, MaximizeSmall, MinimiseSmall } from '@wix/wix-ui-icons-common';
import React, { FC, useCallback } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { TLocalesKeys } from '../../../../types';
import {
  logArticleExternalLinkAction,
  logArticleOpenInNewTabAction,
  logArticleSectionAction,
  logCloseFullPageSidebarAction,
  logWidgetMaximizeMinimizeAction,
} from '../../../bi';
import { helpCenterSlice } from '../../../common/reducers/helpCenter';
import { selectArticle, selectFullPageSidebarExtended } from '../../../common/selectors/helpCenter';
import { appRoutes } from '../../../routes';
import { renderModel } from '../../../util/renderModel';
import { articleSidebarDataHooks } from './ArticleSidebar.dataHooks';

const isMobile = renderModel.isMobile();

export const ArticleSidebarActions: FC = () => {
  const store = useStore();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const article = useSelector(selectArticle);
  const sidebarExtended = useSelector(selectFullPageSidebarExtended);
  const isNewChatFullPage = !renderModel.isCopilot();
  const extendedTooltipTextKey: TLocalesKeys = sidebarExtended
    ? 'sidebar-action-minimize-article'
    : 'sidebar-action-maximize-article';

  const closeArticle = useCallback(() => {
    if (!article) {
      return;
    }

    const state = store.getState();
    logCloseFullPageSidebarAction(state, article);
    navigate(appRoutes.home());
  }, [store, navigate, article]);

  const toggleSidebarExtended = useCallback(() => {
    const isFullWidth = renderModel.isFullWidth();
    if (!article) {
      return;
    }

    const state = store.getState();
    const actionData = {
      expand: !sidebarExtended,
      linkText: article.title,
      articleId: article.id,
    };

    if (isFullWidth) {
      logWidgetMaximizeMinimizeAction(state, actionData);
    } else {
      logArticleSectionAction(state, actionData);
    }

    dispatch(helpCenterSlice.actions.setSidebarExtended(!sidebarExtended));
  }, [store, article, dispatch, sidebarExtended]);

  const articleExternalLinkClicked = useCallback(() => {
    const isFullWidth = renderModel.isFullWidth();
    if (!article) {
      return;
    }

    const state = store.getState();

    if (isFullWidth) {
      logArticleOpenInNewTabAction(state, article);
    } else {
      logArticleExternalLinkAction(state, article);
    }
  }, [article, store]);

  return (
    <Box gap={1}>
      {!isMobile && (
        <Tooltip content={t<TLocalesKeys>(extendedTooltipTextKey)} placement="bottom" size="small">
          <IconButton
            priority="secondary"
            size="tiny"
            skin={isNewChatFullPage ? 'dark' : undefined}
            onClick={toggleSidebarExtended}
            dataHook={articleSidebarDataHooks.toggleModeButton()}
          >
            {sidebarExtended ? <MinimiseSmall /> : <MaximizeSmall />}
          </IconButton>
        </Tooltip>
      )}
      <Tooltip content={t<TLocalesKeys>('sidebar-action-new-tab')} placement="bottom" size="small">
        <IconButton
          priority="secondary"
          size="tiny"
          skin={isNewChatFullPage ? 'dark' : undefined}
          as="a"
          href={article?.url}
          onClick={articleExternalLinkClicked}
          target="_blank"
          dataHook={articleSidebarDataHooks.externalLink()}
        >
          <ExternalLinkSmall />
        </IconButton>
      </Tooltip>
      <Tooltip content={t<TLocalesKeys>('sidebar-action-close')} placement="bottom" size="small">
        <IconButton
          priority="secondary"
          size="tiny"
          skin={isNewChatFullPage ? 'dark' : undefined}
          onClick={closeArticle}
          dataHook={articleSidebarDataHooks.closeButton()}
        >
          <DismissSmall />
        </IconButton>
      </Tooltip>
    </Box>
  );
};
